import { type Row } from "@tanstack/react-table";
import { TrashIcon } from "vega-icons";

import { type CollaboratorPermissionEntry } from "@vega-api/generated/types/collaboratorPermissionEntry";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import {
  ThemedDropdown,
  type ThemedDropdownOptionType,
} from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { m } from "@vega-core/paraglide/messages";

import {
  type EditedPermission,
  FUND_VEHICLE_TAB,
  type FundVehicleTab,
  fundVehicleTagConfig,
  getFundVehicleTabFromPermission,
  getFundVehicleTag,
} from "../components/collaboratorsV2Constants";

export type CellState = {
  markedForDeletion: Set<string>;
  editedPermissions: Record<string, EditedPermission>;
  pendingCreationRowKeys: Set<string>;
  getRowKey: (permission: CollaboratorPermissionEntry) => string;
  onToggleDeletion: (rowKey: string) => void;
  onRemoveCreation: (rowKey: string) => void;
  onEditPermission: (
    rowKey: string,
    updates: Partial<EditedPermission>,
  ) => void;
  getRoleNameById: (roleId: string) => string;
  getRoleOptionsForTab: (tab: FundVehicleTab) => ThemedDropdownOptionType[];
  getEffectiveFundVehicleTab: (
    permission: CollaboratorPermissionEntry,
  ) => FundVehicleTab;
  getDisabledRoleIdsForTab: (
    tab: FundVehicleTab,
    excludeRowKey?: string,
  ) => string[];
};

const getFundVehicleOptions = (): ThemedDropdownOptionType[] =>
  Object.values(FUND_VEHICLE_TAB).map((tab) => ({
    label: fundVehicleTagConfig[tab].label(),
    value: tab,
  }));

export const EditableRoleCell = ({
  row,
  state,
}: {
  row: Row<CollaboratorPermissionEntry>;
  state: CellState;
}) => {
  const rowKey = state.getRowKey(row.original);
  const originalRoleId = row.original.roleId ?? "";

  if (state.markedForDeletion.has(rowKey)) {
    return (
      <span className="text-text/negative line-through">
        {state.getRoleNameById(originalRoleId)}
      </span>
    );
  }

  const hasBeenEdited = rowKey in state.editedPermissions;
  const currentValue = hasBeenEdited
    ? state.editedPermissions[rowKey].roleId
    : originalRoleId;

  const currentTab = state.getEffectiveFundVehicleTab(row.original);
  const disabledRoleIds = new Set(
    state.getDisabledRoleIdsForTab(currentTab, rowKey),
  );
  const filteredOptions = state
    .getRoleOptionsForTab(currentTab)
    .map((option) => ({
      ...option,
      disabled: disabledRoleIds.has(option.value),
    }));
  const isCurrentValueInOptions = filteredOptions.some(
    (option) => option.value === currentValue,
  );

  const options =
    isCurrentValueInOptions || !currentValue
      ? filteredOptions
      : [
          { label: state.getRoleNameById(currentValue), value: currentValue },
          ...filteredOptions,
        ];

  return (
    <ThemedDropdown
      buttonProps={
        currentValue ? undefined : { className: "!border-border/negative" }
      }
      isClearable={false}
      name={`role-${rowKey}`}
      onChange={(option) => {
        if (option) {
          state.onEditPermission(rowKey, { roleId: option.value });
        }
      }}
      options={options}
      value={currentValue || undefined}
    />
  );
};

export const EditableFundVehicleCell = ({
  row,
  state,
}: {
  row: Row<CollaboratorPermissionEntry>;
  state: CellState;
}) => {
  const rowKey = state.getRowKey(row.original);
  const originalRoleId = row.original.roleId ?? "";

  if (state.markedForDeletion.has(rowKey)) {
    return (
      <span className="opacity-50">
        {getFundVehicleTag(getFundVehicleTabFromPermission(row.original))}
      </span>
    );
  }

  const hasBeenEdited = rowKey in state.editedPermissions;
  const currentValue = hasBeenEdited
    ? state.editedPermissions[rowKey].fundVehicleTab
    : getFundVehicleTabFromPermission(row.original);

  return (
    <ThemedDropdown
      isClearable={false}
      name={`fundVehicle-${rowKey}`}
      onChange={(option) => {
        if (option) {
          const newTab = option.value as FundVehicleTab;
          const currentRoleId = hasBeenEdited
            ? state.editedPermissions[rowKey].roleId
            : originalRoleId;
          const newTabOptions = state.getRoleOptionsForTab(newTab);
          const isRoleValidForNewTab = newTabOptions.some(
            (entry) => entry.value === currentRoleId,
          );

          state.onEditPermission(rowKey, {
            fundVehicleTab: newTab,
            ...(isRoleValidForNewTab ? {} : { roleId: "" }),
          });
        }
      }}
      options={getFundVehicleOptions()}
      renderOption={(option) =>
        getFundVehicleTag(option.value as FundVehicleTab)
      }
      value={currentValue}
    />
  );
};

export const DeleteActionCell = ({
  row,
  state,
}: {
  row: Row<CollaboratorPermissionEntry>;
  state: CellState;
}) => {
  const rowKey = state.getRowKey(row.original);

  if (state.pendingCreationRowKeys.has(rowKey)) {
    return (
      <ThemedIconButton
        ariaLabel={m.common_delete()}
        dataTestId={`delete-role-${row.original.roleId}`}
        icon={<TrashIcon className="w-4 h-4" />}
        onClick={() => state.onRemoveCreation(rowKey)}
        size="medium"
        variant="background"
      />
    );
  }

  if (state.markedForDeletion.has(rowKey)) {
    return (
      <ThemedButton
        className="text-text/secondary"
        dataTestId={`undo-delete-role-${row.original.roleId}`}
        onClick={() => state.onToggleDeletion(rowKey)}
        type="button"
        variant="secondary"
      >
        {m.btn_undo()}
      </ThemedButton>
    );
  }

  return (
    <ThemedIconButton
      ariaLabel={m.common_delete()}
      dataTestId={`delete-role-${row.original.roleId}`}
      icon={<TrashIcon className="w-4 h-4" />}
      onClick={() => state.onToggleDeletion(rowKey)}
      size="medium"
      variant="background"
    />
  );
};
